import { useMemo, useState } from "react"
import React from "react"

import { useQuery } from "@tanstack/react-query"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnFiltersState,
} from "@tanstack/react-table"
import { Edit, Plus, X } from "lucide-react"

import Paginator from "@/components/Paginator.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "@/components/ui/dialog.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import type { Identified } from "@/types/commonTypes.ts"

import { filterFn } from "../util/filterFn.ts"

import FilterDisplay from "./FilterDisplay.tsx"

import type { AdminTableConfig } from "../types/adminTableTypes.ts"

export default function AdminTable<T extends Identified>({
  config,
}: {
  config: AdminTableConfig<T>
}) {
  const { data, isPending } = useQuery({
    queryKey: [config.name],
    queryFn: config.fetcher,
  })
  const columnHelper = useMemo(() => createColumnHelper<T>(), [])
  const [newOpen, setNewOpen] = useState<boolean>(false)
  const initialFilters: ColumnFiltersState = []
  config.columns.forEach((col) => {
    if (col.filter) {
      if (col.type == "date") {
        initialFilters.push({
          id: col.name as string,
          value: undefined,
        })
      } else {
        initialFilters.push({
          id: col.name as string,
          value: [],
        })
      }
    }
  })

  const columnDefs = useMemo(() => {
    const dataCols = config.columns.map((col) =>
      columnHelper.accessor(
        (v: T) => (col.type == "date" ? new Date(v[col.name] as string) : v[col.name]),
        {
          id: col.name as string,
          filterFn: (row, colId, filterValue) =>
            filterFn(row, colId, filterValue, col.type == "date"),

          enableColumnFilter: col.filter,
          header: ({ column }) => (
            <div className="p-4" style={col.width ? { width: `${col.width}px` } : {}}>
              {col.filter ? (
                <FilterDisplay
                  column={column}
                  label={col.heading}
                  displayFunc={col.displayFunc}
                  variant={col.type == "date" ? "date" : "multi"}
                />
              ) : (
                <div className="px-2">{col.heading}</div>
              )}
            </div>
          ),
          cell: (context) => (
            <div className="p-2 px-6" style={col.width ? { width: `${col.width}px` } : {}}>
              {col.displayFunc(context.getValue() as T[keyof T])}
            </div>
          ),
          footer: () => <TableCell></TableCell>,
        },
      ),
    )
    const saveCol = columnHelper.display({
      id: "save",
      header: () => <div className="w-[150px] text-center">Edit</div>,
      cell: ({ row }) => (
        <div className="text-center w-[150px]">
          <Button
            variant="ghost"
            onClick={() => {
              row.toggleExpanded()
            }}
          >
            {row.getIsExpanded() ? <X /> : <Edit />}
          </Button>
        </div>
      ),
      footer: () => (
        <div className="text-center">
          <DialogTrigger asChild>
            <Button variant="default">
              <Plus /> Add New
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>Add New</DialogHeader>
            <config.editor onSave={() => setNewOpen(false)} />
          </DialogContent>
        </div>
      ),
    })
    return [...dataCols, saveCol]
  }, [config, columnHelper])

  const table = useReactTable<T>({
    data: data || [],
    columns: columnDefs,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: config.pageSize || 15,
      },
      columnFilters: initialFilters,
    },
  })

  return (
    <Dialog open={newOpen} onOpenChange={setNewOpen}>
      <Table>
        {table.getHeaderGroups().map((hg) => (
          <TableHeader className="bg-accent sticky top-0" key={hg.id}>
            <TableRow>
              {hg.headers.map((header, i) => (
                <TableHead key={i}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        ))}
        <TableBody>
          {isPending
            ? [...Array(config.pageSize || 15).keys()].map((i) => (
                <TableRow key={i}>
                  <TableCell colSpan={table.getAllColumns().length} className="h-[52.5px]">
                    <Skeleton className="w-full h-full" />
                  </TableCell>
                </TableRow>
              ))
            : table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.original.id}>
                  <TableRow>
                    {row.getVisibleCells().map((cell, i) => (
                      <TableCell key={i}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && (
                    <TableRow>
                      <TableCell colSpan={row.getAllCells().length} className="p-4">
                        <config.editor value={row.original} id={row.original.id} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
          <TableRow>
            <TableCell colSpan={table.getAllColumns().length}>
              <DialogTrigger asChild>
                <Button variant="ghost" className="w-full">
                  <Plus /> Add New
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>Add New</DialogHeader>
                <config.editor onSave={() => setNewOpen(false)} />
              </DialogContent>
            </TableCell>
          </TableRow>
        </TableBody>
        <TableFooter className="sticky bottom-0 bg-muted">
          <TableRow>
            <TableCell colSpan={table.getAllColumns().length} className="sticky bottom-0">
              <Paginator table={table} />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Dialog>
  )
}
